/* HeroVariants.jsx — 6 alternative hero designs for verqo seller landing.
 *
 * Each component is a drop-in replacement for the existing hero <section>
 * inside LandingV2Preview.jsx. Mounted via the `heroVariant` prop, e.g.:
 *
 *   <LandingV2Preview heroVariant="combo" ... />
 *
 * Visual scaffolding (max-width, padding, eyebrow, CTAs) matches the live
 * hero so the rest of the page reads correctly underneath.
 */
import { useEffect, useState } from "react";
import {
  ArrowRight, CheckCircle2, Banknote, Eye, ShieldCheck, Layers, Zap,
} from "lucide-react";

// ── shared atoms ─────────────────────────────────────────────────────────
const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium tracking-wide">
    <span className="w-1.5 h-1.5 rounded-full bg-lime-500"/>
    {children}
  </div>
);

const PrimaryCTA = ({ href = "/register?as=seller", children = "Get paid today" }) => (
  <a href={href} className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-all"
     style={{ background: "var(--brand-fg, #0c1612)" }}>
    {children}
    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
  </a>
);

const SecondaryCTA = ({ href = "#how", children = "See how it works" }) => (
  <a href={href} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 text-slate-900 text-sm hover:bg-slate-50 transition-colors">
    {children}
  </a>
);

// 4 value-prop pillars — shared by Patterns 2, 4, and the combo
const PILLARS = [
  { icon: Banknote,    label: "D+1 payouts",          desc: "Sell on Net-60. Get paid tomorrow.", href: "#how" },
  { icon: Eye,         label: "Buyer 360",            desc: "Health, performance, payment history — daily.", href: "#why-verqo" },
  { icon: ShieldCheck, label: "Every invoice insured", desc: "Allianz-backed cover, baked in.", href: "#coverage" },
  { icon: Layers,      label: "One portal, end-to-end", desc: "Orders, claims, payments, shipping — together.", href: "#how" },
];

// Word-morph hook — cycles through an array of words every `ms` ms.
function useMorph(words, ms = 2500) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % words.length), ms);
    return () => clearInterval(t);
  }, [words, ms]);
  return [words[i], i];
}

// ─────────────────────────────────────────────────────────────────────────
// PATTERN 1 — Word swap inside fixed headline (Linear / Webflow style)
// ─────────────────────────────────────────────────────────────────────────
export function HeroPattern1() {
  const words = ["paid in 24h", "covered end-to-end", "risk-checked", "fully automated"];
  const [word, idx] = useMorph(words, 2500);
  // Colour per word for visual life
  const colors = ["bg-lime-300", "bg-amber-200", "bg-sky-200", "bg-violet-200"];
  return (
    <section className="relative pt-32 md:pt-40 pb-20 md:pb-24 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="hero-pattern-1">
      <div className="max-w-3xl">
        <Eyebrow>For brands, wholesalers &amp; distributors · $1M–$100M</Eyebrow>
        <h1 className="mt-6 text-[44px] sm:text-[56px] md:text-[78px] lg:text-[88px] leading-[0.98] tracking-tight font-semibold text-slate-900">
          Wholesale,<br/>
          <span key={idx} className={`${colors[idx]} px-3 -rotate-1 inline-block my-2 transition-all duration-500 animate-in fade-in slide-in-from-bottom-2`}>
            {word}.
          </span>
        </h1>
        <p className="mt-8 max-w-xl text-[16px] md:text-[18px] leading-relaxed text-slate-600">
          The operating system that runs your entire wholesale business — from buyer onboarding to payout — without the spreadsheets, the chasing, or the risk.
        </p>
        <div className="mt-9 flex flex-wrap gap-3 items-center">
          <PrimaryCTA/>
          <SecondaryCTA/>
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-[12px] text-slate-500">
          <div className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600"/> Same-day cash on every invoice</div>
          <div className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600"/> Allianz-backed coverage</div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PATTERN 2 — Umbrella headline + 4-pillar strip (Stripe style)
// ─────────────────────────────────────────────────────────────────────────
export function HeroPattern2() {
  return (
    <section className="relative pt-32 md:pt-40 pb-12 md:pb-16 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="hero-pattern-2">
      <div className="max-w-4xl">
        <Eyebrow>For brands, wholesalers &amp; distributors · $1M–$100M</Eyebrow>
        <h1 className="mt-6 text-[42px] sm:text-[54px] md:text-[72px] lg:text-[80px] leading-[0.98] tracking-tight font-semibold text-slate-900">
          Wholesale, <span className="bg-lime-300 px-3 -rotate-1 inline-block">fully managed.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-[16px] md:text-[19px] leading-relaxed text-slate-600">
          Cashflow, coverage, and a 360 view of every buyer — one portal for serious brands.
        </p>
        <div className="mt-9 flex flex-wrap gap-3 items-center">
          <PrimaryCTA/>
          <SecondaryCTA/>
        </div>
      </div>
      {/* 4-pillar strip */}
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {PILLARS.map((p) => (
          <a key={p.label} href={p.href}
             className="group rounded-2xl bg-white border border-slate-200 p-5 hover:border-slate-900 hover:shadow-[0_12px_40px_-20px_rgba(15,23,42,0.25)] transition-all"
             data-testid={`pillar-${p.label.toLowerCase().replace(/\s/g,'-')}`}>
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-slate-900 text-lime-300 mb-3 group-hover:scale-105 transition-transform">
              <p.icon className="w-5 h-5" strokeWidth={1.8}/>
            </div>
            <div className="font-semibold text-slate-900 text-[15px]">{p.label}</div>
            <div className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">{p.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PATTERN 3 — Static hero + rotating product visual (Ramp style)
// ─────────────────────────────────────────────────────────────────────────
function ProductCarousel() {
  const screens = [
    { tag: "BUYER 360", title: "Health • Performance • Payment", color: "bg-sky-100",   accent: "text-sky-700" },
    { tag: "WALLET",    title: "$84,200 available · D+1 payout", color: "bg-lime-100",  accent: "text-lime-700" },
    { tag: "COVERAGE",  title: "Every invoice · Allianz insured",color: "bg-amber-100", accent: "text-amber-700" },
    { tag: "PORTAL",    title: "Orders → Ship → Pay · 1 ledger",  color: "bg-violet-100",accent: "text-violet-700" },
  ];
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI(x => (x+1) % screens.length), 2500); return () => clearInterval(t); }, []);
  const s = screens[i];
  return (
    <div className={`relative rounded-2xl ${s.color} p-7 min-h-[320px] flex flex-col justify-between border border-slate-200 transition-colors duration-500`}>
      <div>
        <div className={`text-[10px] mono uppercase tracking-[0.2em] ${s.accent} font-semibold`}>{s.tag}</div>
        <div className="mt-3 text-[20px] md:text-[22px] font-semibold text-slate-900 tracking-tight leading-tight">{s.title}</div>
      </div>
      <div className="flex gap-1.5">
        {screens.map((_, j) => (
          <div key={j} className={`h-1 rounded-full transition-all ${j === i ? "w-8 bg-slate-900" : "w-3 bg-slate-300"}`}/>
        ))}
      </div>
    </div>
  );
}
export function HeroPattern3() {
  return (
    <section className="relative pt-32 md:pt-40 pb-20 md:pb-24 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="hero-pattern-3">
      <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-12 lg:gap-16 items-center">
        <div>
          <Eyebrow>For brands, wholesalers &amp; distributors</Eyebrow>
          <h1 className="mt-6 text-[40px] sm:text-[52px] md:text-[72px] lg:text-[80px] leading-[0.98] tracking-tight font-semibold text-slate-900">
            Wholesale, <span className="bg-lime-300 px-3 -rotate-1 inline-block">fully managed.</span>
          </h1>
          <p className="mt-8 max-w-xl text-[16px] md:text-[18px] leading-relaxed text-slate-600">
            One platform for cashflow, coverage, monitoring, and ops. Built for $1M–$100M brands selling wholesale.
          </p>
          <div className="mt-9 flex flex-wrap gap-3 items-center">
            <PrimaryCTA/>
            <SecondaryCTA/>
          </div>
        </div>
        <ProductCarousel/>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PATTERN 4 — Bento grid as the hero (Notion / Linear style)
// ─────────────────────────────────────────────────────────────────────────
export function HeroPattern4() {
  return (
    <section className="relative pt-32 md:pt-40 pb-20 md:pb-24 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="hero-pattern-4">
      <div className="max-w-4xl">
        <Eyebrow>For brands, wholesalers &amp; distributors · $1M–$100M</Eyebrow>
        <h1 className="mt-6 text-[40px] sm:text-[54px] md:text-[72px] leading-[0.98] tracking-tight font-semibold text-slate-900">
          The OS for wholesale.
        </h1>
        <p className="mt-6 max-w-xl text-[16px] md:text-[18px] leading-relaxed text-slate-600">
          Four superpowers, one portal. Pick the one that hurts the most.
        </p>
        <div className="mt-7 flex flex-wrap gap-3"><PrimaryCTA/><SecondaryCTA/></div>
      </div>
      {/* Bento — big tile for hero pillar, 3 smaller */}
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 md:row-span-2 rounded-3xl bg-[#0c1612] text-white p-8 md:p-10 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
               style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}/>
          <div className="relative">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-lime-300 text-slate-900 mb-4">
              <Banknote className="w-6 h-6" strokeWidth={1.6}/>
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-lime-300 font-semibold mb-1">D+1 PAYOUTS</div>
            <h3 className="text-[26px] md:text-[32px] font-semibold tracking-tight leading-[1.05]">Same-day cash on every invoice.</h3>
            <p className="mt-3 text-[14px] text-white/65 leading-relaxed max-w-md">Sell on Net-60. Get paid tomorrow. We carry the float. You keep the buyer relationship.</p>
          </div>
        </div>
        {[PILLARS[1], PILLARS[2], PILLARS[3]].map((p) => (
          <div key={p.label} className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-slate-900 text-lime-300 mb-3">
              <p.icon className="w-5 h-5" strokeWidth={1.8}/>
            </div>
            <div className="text-[10px] mono uppercase tracking-wider text-slate-400">{p.label}</div>
            <div className="mt-1 font-semibold text-slate-900 text-[16px] leading-tight">{p.desc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PATTERN 5 — Headline with morphing icon-word (Mercury / Apple style)
// ─────────────────────────────────────────────────────────────────────────
export function HeroPattern5() {
  const items = [
    { word: "paid",     icon: Banknote,    color: "text-lime-600"   },
    { word: "covered",  icon: ShieldCheck, color: "text-amber-600"  },
    { word: "tracked",  icon: Eye,         color: "text-sky-600"    },
    { word: "automated",icon: Zap,         color: "text-violet-600" },
  ];
  const [w, idx] = useMorph(items, 2200);
  return (
    <section className="relative pt-32 md:pt-40 pb-20 md:pb-24 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="hero-pattern-5">
      <div className="max-w-3xl">
        <Eyebrow>For brands, wholesalers &amp; distributors</Eyebrow>
        <h1 className="mt-6 text-[42px] sm:text-[56px] md:text-[80px] lg:text-[92px] leading-[0.98] tracking-tight font-semibold text-slate-900">
          Wholesale, but{" "}
          <span key={idx} className="inline-flex items-center gap-3 align-baseline transition-all animate-in fade-in slide-in-from-bottom-2 duration-500">
            <w.icon className={`${w.color} inline-block`} style={{ height: "0.85em", width: "0.85em" }} strokeWidth={2}/>
            <span className={w.color}>{w.word}</span>
          </span>.
        </h1>
        <p className="mt-8 max-w-xl text-[16px] md:text-[18px] leading-relaxed text-slate-600">
          One platform for cashflow, risk, monitoring, and ops. Built for the brands quietly running circles around their competitors.
        </p>
        <div className="mt-9 flex flex-wrap gap-3 items-center">
          <PrimaryCTA/>
          <SecondaryCTA/>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// COMBO — Pattern 1 (word-morph) + Pattern 2 (4-pillar strip) — my pick
// ─────────────────────────────────────────────────────────────────────────
export function HeroCombo() {
  const words = ["paid in 24h", "covered end-to-end", "risk-checked", "fully automated"];
  const [word, idx] = useMorph(words, 2500);
  return (
    <section className="relative pt-32 md:pt-40 pb-12 md:pb-16 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="hero-combo">
      <div className="max-w-4xl">
        <Eyebrow>For brands, wholesalers &amp; distributors · $1M–$100M</Eyebrow>
        <h1 className="mt-6 text-[42px] sm:text-[56px] md:text-[78px] lg:text-[88px] leading-[0.98] tracking-tight font-semibold text-slate-900">
          Wholesale,<br/>
          <span key={idx} className="bg-lime-300 px-3 -rotate-1 inline-block my-2 transition-all duration-500 animate-in fade-in slide-in-from-bottom-2">
            {word}.
          </span>
        </h1>
        <p className="mt-8 max-w-2xl text-[16px] md:text-[19px] leading-relaxed text-slate-600">
          The operating system for serious brands selling wholesale — cashflow, coverage, and a 360 view of every buyer, in one portal.
        </p>
        <div className="mt-9 flex flex-wrap gap-3 items-center">
          <PrimaryCTA/>
          <SecondaryCTA/>
        </div>
      </div>
      {/* 4-pillar strip — same as Pattern 2 */}
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {PILLARS.map((p) => (
          <a key={p.label} href={p.href}
             className="group rounded-2xl bg-white border border-slate-200 p-5 hover:border-slate-900 hover:shadow-[0_12px_40px_-20px_rgba(15,23,42,0.25)] transition-all">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-slate-900 text-lime-300 mb-3 group-hover:scale-105 transition-transform">
              <p.icon className="w-5 h-5" strokeWidth={1.8}/>
            </div>
            <div className="font-semibold text-slate-900 text-[15px]">{p.label}</div>
            <div className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">{p.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

// Map for the LandingV2Preview to consume via `heroVariant` prop.
export const HERO_VARIANTS = {
  "pattern-1": HeroPattern1,
  "pattern-2": HeroPattern2,
  "pattern-3": HeroPattern3,
  "pattern-4": HeroPattern4,
  "pattern-5": HeroPattern5,
  "combo":     HeroCombo,
  // v2-final · 3 strategies — keep existing hero diagram/stats/checkmarks
  // UNCHANGED; only swap the rotating green word + the sub-hero paragraph.
  "v2-a":      HeroV2StrategyA,
  "v2-b":      HeroV2StrategyB,
  "v2-c":      HeroV2StrategyC,
  "v2-final":  HeroV2Final,
};

// ═════════════════════════════════════════════════════════════════════════
// V2 — Final 3 strategies (user picked Strategy A copy direction for the
// sub-hero; this set is for comparing the ROTATING WORD options A vs B vs C).
//
// Keeps the EXISTING hero scaffolding intact:
//   • Eyebrow
//   • H1 with first line "Wholesale," + green rotating highlight + period
//   • NetworkGraph card on the right (imported from LandingV2Preview)
//   • Stats row below
//   • Promise checkmarks
//   • CTAs unchanged
//
// Only differences between A / B / C:
//   • The 4 rotating words/phrases
//   • Same sub-hero copy across all three (the user already picked the
//     outcome-led umbrella line — Option A from the prior choice)
// ═════════════════════════════════════════════════════════════════════════
import { NetworkGraph } from "./LandingV2Preview";

const SUB_HERO_COPY = (
  <>
    Get paid tomorrow, covered on every invoice, and never blind to a buyer — <strong className="text-slate-900">the operating system for serious wholesale brands.</strong>
  </>
);

const PROMISE_CHIPS = (
  <>
    <div className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600"/> Same-day cash on every invoice</div>
    <div className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600"/> Zero default risk — Allianz insured</div>
  </>
);

const HERO_STATS = [
  ["500+",            "buyers credentialed on day 1"],
  ["Net-30/60/90/120", "approved in seconds"],
  ["<60 sec",          "credit decisions"],
  ["0 days",           "DSO on funded invoices"],
];

function HeroV2Scaffold({ rotatingWords, testid, subHero }) {
  const [word, idx] = useMorph(rotatingWords, 2500);
  return (
    <section className="relative pt-32 md:pt-40 pb-20 md:pb-24 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid={testid}>
      <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-12 lg:gap-16 items-center">
        {/* LEFT — copy + CTAs */}
        <div>
          <Eyebrow>For brands, wholesalers &amp; distributors · $1M–$100M</Eyebrow>
          <h1 className="mt-6 text-[36px] sm:text-[44px] md:text-[68px] lg:text-[80px] leading-[1.02] sm:leading-[0.98] md:leading-[0.96] tracking-tight font-semibold text-slate-900">
            Wholesale,<br/>
            <span key={idx} className="bg-lime-300 px-2.5 sm:px-3 -rotate-1 inline-block my-2 sm:my-3 transition-all duration-500 animate-in fade-in slide-in-from-bottom-2">
              {word}
            </span>
          </h1>
          <p className="mt-8 max-w-xl text-[16px] md:text-[18px] leading-relaxed text-slate-600">
            {subHero || SUB_HERO_COPY}
          </p>
          <div className="mt-9 flex flex-wrap gap-3 items-center">
            <PrimaryCTA/>
            <SecondaryCTA/>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-[12px] text-slate-500">
            {PROMISE_CHIPS}
          </div>
        </div>
        {/* RIGHT — existing NetworkGraph card UNTOUCHED */}
        <div className="relative rounded-2xl p-6 md:p-7 overflow-hidden shadow-[0_40px_80px_-32px_rgba(15,23,42,0.4)]" style={{ background: "#0c1612" }}>
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
               style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}/>
          <div className="absolute inset-0 opacity-30 pointer-events-none"
               style={{ background: "radial-gradient(circle at 50% 40%, rgba(220,255,58,0.18) 0%, transparent 55%)" }}/>
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.26em] font-semibold" style={{ color: "#dcff3a" }}>The differentiator</div>
              <div className="inline-flex items-center gap-1.5 text-[10px] text-white/55 uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-lime-300 animate-pulse"/> Live
              </div>
            </div>
            <NetworkGraph/>
            <div className="mt-2 text-center">
              <div className="text-[19px] md:text-[21px] font-semibold text-white tracking-tight leading-tight">
                Order. Pay. Ship. Track. Reconcile. <span className="text-white/45">One ledger.</span>
              </div>
              <div className="text-[11.5px] text-white/55 mt-1.5 max-w-[320px] mx-auto leading-relaxed">
                Buyer · seller · lender · insurer · bank · carrier — every player, every step, in real time.
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Stats strip — EXISTING design, preserved */}
      <div className="mt-16 pt-8 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
        {HERO_STATS.map(([v, l]) => (
          <div key={l}>
            <div className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 tabular-nums">{v}</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mt-1.5">{l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// Strategy A — Direct & ownable (recommended)
export function HeroV2StrategyA() {
  return <HeroV2Scaffold testid="hero-v2-a"
    rotatingWords={["paid today.", "buyer-verified.", "default-proof.", "on autopilot."]}/>;
}

// Strategy B — Cinematic / premium
export function HeroV2StrategyB() {
  return <HeroV2Scaffold testid="hero-v2-b"
    rotatingWords={["paid the moment you sell.", "with eyes on every buyer.", "covered on every invoice.", "fully automated, end-to-end."]}/>;
}

// Strategy C — Provocative / bold (single-word)
export function HeroV2StrategyC() {
  return <HeroV2Scaffold testid="hero-v2-c"
    rotatingWords={["DSO-free.", "buyer-aware.", "default-proof.", "self-running."]}/>;
}

// FINAL — Strategy B rotating words + user's custom sub-hero (approval pending)
export function HeroV2Final() {
  const customSubHero = (
    <>
      Run your entire wholesale business from one portal:{" "}
      <strong className="text-slate-900">Day 1 payouts, Insurance-backed cover, real-time buyer monitoring, and end-to-end automation.</strong>
    </>
  );
  return <HeroV2Scaffold
    testid="hero-v2-final"
    rotatingWords={["paid the moment you sell.", "with eyes on every buyer.", "covered on every invoice.", "fully automated, end-to-end."]}
    subHero={customSubHero}
  />;
}

